"use client"
import React, { useState } from 'react'
import Image from 'next/image'
import { apiSubscribeNewsletter } from '@/apis/page_optimization';
import { toast } from 'react-toastify';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { apiVerifyCaptcha } from '@/apis/captcha';

interface Props {
    companyId: string;
    domain: string;
}

const Newsletter = ({ domain, companyId }: Props) => {
    const [email, setEmail] = useState<string>("");
    const [status, setStatus] = useState<'idle' | 'verifying' | 'sending' | 'failed'>('idle');

    const buttonLabel = {
        idle: 'Subscribe',
        verifying: 'Verifying...',
        sending: 'Subscribing...',
        failed: 'Failed',
    }[status];

    const { executeRecaptcha } = useGoogleReCaptcha();

    const validateEmail = (emailStr: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(emailStr);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email.trim()) {
            toast.error('Email field is required.', { autoClose: 2000 });
            return;
        }

        if (!validateEmail(email)) {
            toast.error("Please enter a valid email address.", { autoClose: 2000 });
            return;
        }

        if (!executeRecaptcha) {
            toast.error('reCAPTCHA not ready. Please try again.', { autoClose: 2000 });
            return;
        }

        try {
            setStatus('verifying');
            const recaptchaToken = await executeRecaptcha('newsletter_form');
            const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken);

            if (!captchaRes?.verified) {
                setStatus('failed');
                toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 });
                return;
            }

            setStatus('sending');
            const response = await apiSubscribeNewsletter(companyId, email);

            if (response?.status === 200 || response?.status === true || response?.message === "Subscribed successfully") {
                toast.success("Thank you for subscribing!", { autoClose: 2000 });
                setEmail("");
            } else {
                toast.error(response?.message || "You have already subscribed to our newsletter.");
            }
        } catch (error) {
            console.error("Error subscribing:", error);
            toast.error('Failed to subscribe. Please try again later.', { autoClose: 2000 });
        } finally {
            setStatus('idle');
        }
    };

    return (
        <section className="newsletter position-relative z-index-1 overflow-hidden">
            <Image
                src="/themes/Theme_1/images/gradients/newsletter-gradient-bg.png"
                height={300}
                width={300}
                alt="gradient"
                className="bg--gradient"
            />
            <Image
                src="/themes/Theme_1/images/shapes/line-vector-one.png"
                height={300}
                width={300}
                alt="vector one"
                className="line-vector one"
            />
            <Image
                src="/themes/Theme_1/images/shapes/line-vector-two.png"
                height={300}
                width={300}
                alt="vector two"
                className="line-vector two"
            />
            <div className="container container-two">
                <div className="row justify-content-center" style={{ padding: "20px 0 20px 0" }}>
                    <div className="col-xl-6 col-lg-8 col-md-10">
                        <div className="newsletter-content">
                            <h2 className="newsletter-content__title mb-2 text-center f-30" style={{ color: "white" }}>
                                Subscribe to our newsletter to get the best deals right in your email.
                            </h2>
                            <form
                                onSubmit={handleSubmit}
                                autoComplete="off"
                                className="mt-4 newsletter-box position-relative"
                            >
                                <input
                                    type="email"
                                    className="form-control common-input common-input--lg pill text-white news-input-box"
                                    placeholder="Enter your email."
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <button
                                    type="submit"
                                    className="btn btn-main btn-lg pill flx-align gap-1"
                                    disabled={status !== 'idle'}
                                >
                                    {buttonLabel} <span className="text d-sm-flex d-none">Now</span>
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Newsletter;
